'use client';

import React, { useState } from 'react';
import { ECO_TILES } from './ecoData';


interface SimilarAyatSectionProps {
  currentTileId?: string;
  currentCity: string;
}

interface CrossCityGroup {
  id: string;
  nameAr: string;
  nameEn: string;
  description: string;
  tiles: Array<{ id: string; city: string; title: string; cat: string }>;
}

const BRIDGE_TYPE_PILLS: Record<string, { label: string; color: string; bg: string; border: string }> = {
  name: { label: 'SHARED NAME', color: '#c9a84c', bg: 'rgba(201,168,76,0.08)', border: 'rgba(201,168,76,0.2)' },
  process: { label: 'SAME PROCESS', color: '#2d8a87', bg: 'rgba(45,138,135,0.08)', border: 'rgba(45,138,135,0.2)' },
  cycle: { label: 'SHARED CYCLE', color: '#2d7a47', bg: 'rgba(45,122,71,0.08)', border: 'rgba(45,122,71,0.2)' },
  amanah: { label: 'SHARED TRUST', color: '#8b7335', bg: 'rgba(139,115,53,0.08)', border: 'rgba(139,115,53,0.2)' },
};

const CROSS_CITY_GROUPS: CrossCityGroup[] = [
  {
    id: 'nur-razzaq-photosynthesis',
    nameAr: 'النور / الرزاق',
    nameEn: 'An-Nur / Ar-Razzaq / Photosynthesis',
    description: 'The same divine process — sunlight converted to provision — operates in Atlanta\'s forest canopy (C3), Chicago\'s tallgrass prairie (C4), and Detroit\'s wetland algae (aquatic). Same Amr, three biome dialects.',
    tiles: [
      { id: 'ae3', city: 'Atlanta', title: 'Canopy Photosynthesis', cat: 'Process' },
      { id: 'ce3', city: 'Chicago', title: 'Prairie Photosynthesis', cat: 'Process' },
      { id: 'de5', city: 'Detroit', title: 'Wetland Algae', cat: 'Process' },
    ],
  },
  {
    id: 'wadud-mycorrhizal',
    nameAr: 'الودود',
    nameEn: 'Al-Wadud / Mycorrhizal Love',
    description: 'Underground networks of provision through love manifest in Atlanta\'s forest roots, Detroit\'s wetland root systems, and Chicago\'s prairie underground carbon.',
    tiles: [
      { id: 'ae5', city: 'Atlanta', title: 'Mycorrhizal Network', cat: 'Process' },
      { id: 'ce2', city: 'Chicago', title: 'Prairie Root Carbon', cat: 'Process' },
      { id: 'de4', city: 'Detroit', title: 'Wetland Root Systems', cat: 'Process' },
    ],
  },
  {
    id: 'jami-gathering-waters',
    nameAr: 'الجامع',
    nameEn: 'Al-Jami / Gathering Waters',
    description: 'Waters gathering — Atlanta\'s Piedmont Creek → Pittsburgh\'s Three Rivers Confluence → Louisville\'s Ohio River → Detroit\'s Great Lakes. One principle of divine gathering across scales.',
    tiles: [
      { id: 'ae6', city: 'Atlanta', title: 'Piedmont Creek', cat: 'Process' },
      { id: 'pe1', city: 'Pittsburgh', title: 'Three Rivers Confluence', cat: 'Process' },
      { id: 'le1', city: 'Louisville', title: 'Ohio River Floodplain', cat: 'Process' },
      { id: 'de1', city: 'Detroit', title: 'Great Lakes', cat: 'Process' },
    ],
  },
  {
    id: 'sabur-baqi-patience',
    nameAr: 'الصبور / الباقي',
    nameEn: 'As-Sabur / Al-Baqi / Patience Made Structure',
    description: 'Patience manifest in living organisms: Atlanta\'s Loblolly Pine (fire ecology patience), Durham\'s Longleaf Grass Stage (7-year foundation), Chicago\'s Big Bluestem (10,000-year root), Cincinnati\'s Coral (1mm/year reef).',
    tiles: [
      { id: 'ae2', city: 'Atlanta', title: 'Loblolly Pine', cat: 'Creation' },
      { id: 'du_e1', city: 'Durham', title: 'Longleaf Grass Stage', cat: 'Creation' },
      { id: 'ce1', city: 'Chicago', title: 'Big Bluestem', cat: 'Creation' },
      { id: 'ci_e3', city: 'Cincinnati', title: 'River Bluff Geology', cat: 'Process' },
    ],
  },
  {
    id: 'hadi-navigation',
    nameAr: 'الهادي',
    nameEn: 'Al-Hadi / Navigation',
    description: 'Divine guidance manifest across three navigation systems: Chicago\'s Monarch butterfly (3,000-mile continental migration), Portland\'s Salmon run (return to birth gravel), Cincinnati\'s Ohio River meander (water guided to ocean).',
    tiles: [
      { id: 'ce8', city: 'Chicago', title: 'Monarch Migration', cat: 'Creation' },
      { id: 'po_e4', city: 'Portland', title: 'Salmon Run', cat: 'Creation' },
      { id: 'ci_e1', city: 'Cincinnati', title: 'Ohio River Meander', cat: 'Process' },
    ],
  },
  {
    id: 'quddus-purification',
    nameAr: 'القدوس',
    nameEn: 'Al-Quddus / Purification',
    description: 'Purification systems in three cities: Detroit\'s wetland filtration (free $500K treatment plant), Cincinnati\'s river mussels (100-year silent filtering), Portland\'s Pacific atmospheric river (5,000-mile distillation).',
    tiles: [
      { id: 'de4', city: 'Detroit', title: 'Wetland Filtration', cat: 'Process' },
      { id: 'ci_e2', city: 'Cincinnati', title: 'River Mussel', cat: 'Creation' },
      { id: 'po_e3', city: 'Portland', title: 'Pacific Atmospheric River', cat: 'Process' },
    ],
  },
];

function BridgePill({ type }: { type: string }) {
  const pill = BRIDGE_TYPE_PILLS[type] || BRIDGE_TYPE_PILLS.name;
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 5px',
      borderRadius: 2,
      fontSize: 7,
      letterSpacing: '.06em',
      color: pill.color,
      background: pill.bg,
      border: `1px solid ${pill.border}`,
      marginRight: 4,
    }}>
      {pill.label}
    </span>
  );
}

function TileCard({ tile, isHighlighted }: { tile: { id: string; city: string; title: string; cat: string }; isHighlighted?: boolean }) {
  const ecoTile = ECO_TILES.find(t => t.id === tile.id);
  const bg = ecoTile?.bg || '#1a2a1a';

  return (
    <div style={{
      flex: '0 0 auto',
      minWidth: 100,
      maxWidth: 140,
      padding: '6px 8px',
      background: bg,
      borderRadius: 4,
      border: isHighlighted ? '1px solid rgba(201,168,76,0.4)' : '1px solid rgba(255,255,255,0.06)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{ fontSize: 7, color: 'rgba(201,168,76,0.5)', letterSpacing: '.06em', marginBottom: 2 }}>{tile.city}</div>
      <div style={{ fontSize: 10, color: 'rgba(240,236,228,0.8)', fontWeight: 600, lineHeight: 1.2 }}>{tile.title}</div>
      <div style={{ fontSize: 7, color: 'rgba(240,236,228,0.3)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '.04em' }}>{tile.cat}</div>
    </div>
  );
}

function ConnectorLine({ hovered }: { hovered: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '0 2px', flexShrink: 0 }}>
      <svg width="24" height="12" viewBox="0 0 24 12">
        <line x1="0" y1="6" x2="18" y2="6"
          stroke={hovered ? 'rgba(201,168,76,0.35)' : 'rgba(201,168,76,0.15)'}
          strokeWidth="1"
          strokeDasharray="3,2"
          style={{ transition: 'stroke 0.3s' }}
        />
        <polygon points="18,3 24,6 18,9"
          fill={hovered ? 'rgba(201,168,76,0.35)' : 'rgba(201,168,76,0.15)'}
          style={{ transition: 'fill 0.3s' }}
        />
      </svg>
    </div>
  );
}

export default function SimilarAyatSection({ currentTileId, currentCity }: SimilarAyatSectionProps) {
  const [expanded, setExpanded] = useState(true);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);

  // Find current tile's bridges if provided
  const currentTile = currentTileId ? ECO_TILES.find(t => t.id === currentTileId) : null;
  const currentBridges = currentTile?.bridges || [];

  return (
    <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 }}>
      {/* SECTION HEADER */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '4px 0',
          marginBottom: expanded ? 8 : 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#2d7a47' }} />
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#2d7a47', letterSpacing: '.12em', textTransform: 'uppercase' }}>
            SIMILAR AYAT ACROSS CITIES
          </span>
          <span style={{ fontFamily: 'serif', fontSize: 11, color: 'rgba(45,122,71,0.5)' }}>آيات متشابهة</span>
        </div>
        <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(255,255,255,0.2)' }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div>
          {/* CONNECTED AYAT — current tile's specific bridges */}
          {currentTile && currentBridges.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
                CONNECTED AYAT — {currentTile.title}
              </div>
              {currentBridges.map((bid: string, i: number) => {
                const bridgeTile = ECO_TILES.find(t => t.id === bid);
                const label = currentTile.bridge_labels?.[i] || bid;
                return (
                  <div key={bid} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 6px',
                    background: 'rgba(45,122,71,0.05)',
                    border: '1px solid rgba(45,122,71,0.1)',
                    borderRadius: 3,
                    marginBottom: 3,
                  }}>
                    <BridgePill type="process" />
                    <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#2d7a47' }}>{label}</span>
                    {bridgeTile && (
                      <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(255,255,255,0.2)', marginLeft: 'auto' }}>{bridgeTile.city}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* CROSS-CITY GROUPS */}
          {CROSS_CITY_GROUPS.map(group => (
            <div
              key={group.id}
              style={{ marginBottom: 12 }}
              onMouseEnter={() => setHoveredGroup(group.id)}
              onMouseLeave={() => setHoveredGroup(null)}
            >
              {/* Group header */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
                <span style={{ fontFamily: 'serif', fontSize: 13, color: '#c9a84c' }}>{group.nameAr}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.06em' }}>{group.nameEn}</span>
              </div>

              {/* Tile row with connectors */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
                {group.tiles.map((tile, i) => (
                  <React.Fragment key={tile.id}>
                    <TileCard
                      tile={tile}
                      isHighlighted={tile.id === currentTileId}
                    />
                    {i < group.tiles.length - 1 && (
                      <ConnectorLine hovered={hoveredGroup === group.id} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Description */}
              <div style={{
                fontFamily: 'monospace',
                fontSize: 9,
                color: 'rgba(255,255,255,0.3)',
                lineHeight: 1.5,
                marginTop: 4,
                paddingLeft: 2,
                borderLeft: '1px solid rgba(45,122,71,0.15)',
                paddingLeft: 6,
              }}>
                {group.description}
              </div>
            </div>
          ))}

          {/* FOOTER */}
          <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(255,255,255,0.1)', textAlign: 'center', paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            The same Names manifest across all biomes — different dialects of one Amr
          </div>
        </div>
      )}
    </div>
  );
}
